import { ArrowLeft, Search, Users } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { airtimeApi } from "../api";
import AirtimeAllocationTable from "../components/AirtimeAllocationTable";
import AirtimeEmptyState from "../components/AirtimeEmptyState";
import AirtimeLoader from "../components/AirtimeLoader";
import { useAirtimeAllocations } from "../hooks";
import { useToast } from "#/components/common/ToastProvider";

export default function AirtimeAllocationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const [savingEmployeeId, setSavingEmployeeId] = useState<number | null>(null);

  // Debounce search — wait 400ms after the user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(
    () => ({
      page,
      size,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [page, size, debouncedSearch],
  );

  const { data, isLoading, isFetching, error } = useAirtimeAllocations(params);

  const mutation = useMutation({
    mutationFn: ({
      employeeId,
      amount,
    }: {
      employeeId: number;
      amount: number;
    }) => {
      setSavingEmployeeId(employeeId);

      return airtimeApi.updateAllocation(employeeId, amount);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["airtime", "allocations"],
      });

      queryClient.invalidateQueries({
        queryKey: ["airtime", "stats"],
      });

      toast.success("Allocation updated successfully");
    },

    onError: (err) => {
      console.error(err);

      toast.error("Failed to update allocation");
    },

    onSettled: () => {
      setSavingEmployeeId(null);
    },
  });

  if (isLoading) {
    return <AirtimeLoader />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-700">
          Failed to load airtime allocations.
        </div>
      </div>
    );
  }

  const allocations = data?.content ?? [];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mb-6 flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
        <div>
          <button
            type="button"
            onClick={() => navigate("/hr/airtime")}
            className="mb-3 inline-flex items-center gap-2 text-sm font-black text-emerald-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Airtime Portal
          </button>

          <h1 className="text-3xl font-black text-slate-900">
            Employee Airtime Allocations
          </h1>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-3.5 h-4 w-4 text-slate-400" />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employee or phone"
              className="w-full rounded-2xl border border-slate-300 py-3 pl-11 pr-4 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 sm:w-80"
            />
          </div>

          <select
            value={size}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          >
            {[10, 25, 50, 100].map((item) => (
              <option key={item} value={item}>
                {item} rows
              </option>
            ))}
          </select>
        </div>
      </div>

      {isFetching && (
        <div className="mb-3 text-sm font-bold text-slate-500">
          Refreshing...
        </div>
      )}

      <div className="mb-4 flex items-center gap-2 rounded-2xl bg-white px-5 py-4 shadow-sm">
        <Users className="h-5 w-5 text-emerald-600" />

        <div className="text-sm font-bold text-slate-700">
          {data?.totalElements ?? 0} employee(s) found
        </div>
      </div>

      {!allocations.length ? (
        <AirtimeEmptyState
          title="No employees found"
          description="No employees matched your search criteria."
        />
      ) : (
        <AirtimeAllocationTable
          data={allocations}
          savingEmployeeId={savingEmployeeId}
          onSave={(employeeId, amount) =>
            mutation.mutate({
              employeeId,
              amount,
            })
          }
        />
      )}

      <Pagination
        page={data?.number ?? page}
        size={data?.size ?? size}
        totalPages={data?.totalPages ?? 0}
        totalElements={data?.totalElements ?? 0}
        onPageChange={setPage}
      />
    </div>
  );
}

function Pagination({
  page,
  size,
  totalPages,
  totalElements,
  onPageChange,
}: {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="mt-5 flex flex-col justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 text-sm sm:flex-row sm:items-center">
      <div className="font-semibold text-slate-600">
        Showing page {totalPages ? page + 1 : 0} of {totalPages}
        {" • "}
        {totalElements} record(s)
        {" • "}
        {size} per page
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 0}
          onClick={() => onPageChange(page - 1)}
          className="rounded-2xl border border-slate-200 px-4 py-2 font-black text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>

        <button
          type="button"
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="rounded-2xl border border-slate-200 px-4 py-2 font-black text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
